/** biome-ignore-all lint/nursery/noRestrictedElements: Trying to prevent bundle issues */
'use client'; // Error boundaries must be Client Components

import { InternalError } from '@/app_components/errors/InternalError';
import { fortuneSocialLinks } from '@/appconstants';
import '@/app_styles/globals.css';
import { registerAppRouterFonts } from 'styles/fonts';
import { FontRegistryStyles } from 'utils/fontRegistry';
import { LoggerTags } from 'utils/log/constants/loggerTags';
import logToBackend from 'utils/log/logToBackend';
import { LogoSvg } from '@/app_components/icons/LogoSvg';
import { Facebook } from '@/app_components/icons/socials/Facebook';
import { Instagram } from '@/app_components/icons/socials/Instagram';
import { LinkedIn } from '@/app_components/icons/socials/LinkedIn';
import { Pinterest } from '@/app_components/icons/socials/Pinterest';
import { Twitter } from '@/app_components/icons/socials/Twitter';
import { FontRegistry } from '@/main/lib/utils/fontRegistryUtils';

/*
 * Global Error has its own code tree in the app router, so it doesn't use the root layout file
 * Also cannot include the Global Scripts component as it is a server component
 * and this is a client component
 */
export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  registerAppRouterFonts();
  FontRegistry.seal();

  // Since React Server Components will only yield the error when caught by its boundary, we find ourselves
  // in a roundabout situation in which an error that has occurred on the server has to be reported back from
  // the client; This is by design in Next's App router, as even though it does log the errors on the server (in its own format),
  // we get no way to hook into them.
  // However, since the errors reach this component, we can send them back to the server through an API call
  logToBackend(LoggerTags.SERVER_ERROR, error.name, {
    message: error.message,
    traceback: error.stack,
    url: window.location.href,
  });

  const socialIcons = {
    facebook: <Facebook className='w-3 h-3' />,
    instagram: <Instagram className='w-3 h-3' />,
    linkedin: <LinkedIn className='w-3 h-3' />,
    pinterest: <Pinterest className='w-3 h-3' />,
    twitter: <Twitter className='w-3 h-3' />,
  };

  return (
    <html lang='en'>
      <head>
        {/* We don't use <FontRegistryPreloadHeaders /> here as this is a Client Component */}
        <FontRegistryStyles />
      </head>
      <body className='antialiased bg-primary text-primary font-graphik-compact typography-default font-normal tracking-none'>
        <div className='min-h-screen flex flex-col justify-between'>
          {/* Fake Header */}
          <header>
            <div className='shadow-[0_15px_30px_0_rgba(0,0,0,0.1)] h-17 w-full flex flex-col items-center justify-center'>
              <a
                aria-label='Go to Fortune.com homepage'
                className='inline-flex flex-col items-start gap-1'
                href='https://fortune.com'
              >
                <LogoSvg className='h-6 block w-auto fill-current' />
              </a>
            </div>
          </header>
          {/* Main Error stuff */}
          <main>
            <InternalError />
          </main>
          <footer className='border-t-1 stroke-inverse px-8 pt-4 w-full'>
            <div className='flex flex-col xl:flex-row-reverse gap-4 pb-4 w-full items-center justify-between'>
              <ul className='flex items-center justify-center gap-2'>
                {fortuneSocialLinks.map(({ href, id }: { href: string; id: string }) => (
                  <li key={href}>
                    <a
                      aria-label={`Go to our ${id} page`}
                      className='border rounded-full w-8 h-8 items-center justify-center flex cursor-pointer hover:text-primary-hover hover:stroke-accent'
                      href={href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {socialIcons[id as keyof typeof socialIcons]}
                    </a>
                  </li>
                ))}
              </ul>
              <div className='typography-xs text-helper'>
                <p>
                  © 2026 Fortune Media IP Limited. All Rights Reserved. Use of this site constitutes acceptance of our{' '}
                  <a
                    className='text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited'
                    href='https://fortune.com/terms-of-use/'
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    className='text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited'
                    href='https://fortune.com/privacy-policy/'
                  >
                    Privacy Policy
                  </a>{' '}
                  |{' '}
                  <a
                    className='text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited'
                    href='/california-privacy-policy/#notice'
                  >
                    CA Notice at Collection and Privacy Notice
                  </a>
                   | {/* TODO: Bring back after finding out if its necessary */}
                  {/* <a
                    className='text-blue-500 dark:text-blue-300 hover:text-primary-hover active:text-primary-pressed visited:text-primary-visited'
                    href='javascript:Optanon.ToggleInfoDisplay();'
                    target='_self'
                  >
                    Do Not Sell/Share My Personal Information
                  </a> */}
                  <br />
                  FORTUNE is a trademark of Fortune Media IP Limited, registered in the U.S. and other countries.
                  FORTUNE may receive compensation for some links to products and services on this website. Offers may
                  be subject to change without notice.
                </p>
              </div>
            </div>
          </footer>
        </div>
      </body>
    </html>
  );
}
